import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import { useCallLog } from '../hooks/useCallLog';

interface CallLogDetailDrawerProps {
  logId: number;
  onClose: () => void;
}

/**
 * CallLogDetailDrawer — 호출 이력 상세 표시 우측 패널 (dialog 상태)
 */
export function CallLogDetailDrawer({ logId, onClose }: CallLogDetailDrawerProps) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { data: detail, isLoading, isError, error } = useCallLog(logId);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 900,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {/* 오버레이 */}
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* 패널 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="calllog-detail-title"
        style={{
          position: 'relative',
          width: 560,
          maxWidth: '95vw',
          height: '100%',
          overflowY: 'auto',
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          padding: 'var(--spacing-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-4)',
        }}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2
            id="calllog-detail-title"
            style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}
          >
            {t('integrationHub.history.detail.title')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={t('integrationHub.history.detail.close')}
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--font-size-lg)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              lineHeight: 1,
              padding: 'var(--spacing-1)',
            }}
          >
            ✕
          </button>
        </div>

        {/* loading skeleton */}
        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: 18,
                  width: `${50 + (i * 10) % 40}%`,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        )}

        {/* error */}
        {isError && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-4)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-danger)',
            }}
          >
            {t(`integrationHub.error.${(error as { code?: string })?.code ?? 'network'}`, {
              defaultValue: t('integrationHub.error.loadFailed'),
            })}
          </div>
        )}

        {/* default — 상세 표시 */}
        {!isLoading && !isError && detail && (
          <>
            {/* 메타 요약 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--spacing-3)',
                padding: 'var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-surface-border)',
              }}
            >
              <InfoField label="ID" value={String(detail.id)} />
              <InfoField
                label={t('integrationHub.history.column.operation')}
                value={detail.operation ? t(`integrationHub.operation.${detail.operation}`, { defaultValue: detail.operation }) : '-'}
              />
              <InfoField
                label={t('integrationHub.endpoint.column.protocol')}
                value={t(`integrationHub.protocol.${detail.protocol}`)}
              />
              <InfoField
                label="결과"
                value={
                  <StatusPill tone={detail.result === 'SUCCESS' ? 'success' : 'danger'}>
                    {t(`integrationHub.result.${detail.result}`)}
                  </StatusPill>
                }
              />
              <InfoField
                label={t('integrationHub.invoke.result.status')}
                value={detail.responseStatus != null ? String(detail.responseStatus) : '-'}
              />
              <InfoField
                label={t('integrationHub.invoke.result.duration')}
                value={detail.durationMs != null ? `${detail.durationMs}ms` : '-'}
              />
              <InfoField
                label={t('integrationHub.history.detail.actor')}
                value={detail.actor ?? '-'}
              />
              <InfoField
                label={t('integrationHub.history.detail.target')}
                value={detail.target ?? '-'}
              />
              {detail.errorCode && (
                <InfoField
                  label="오류 코드"
                  value={
                    <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}>
                      {detail.errorCode}
                    </code>
                  }
                />
              )}
            </div>

            {/* Trace ID */}
            {detail.traceId && (
              <div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                  {t('integrationHub.invoke.result.traceId')}
                </p>
                <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
                  {detail.traceId}
                </code>
              </div>
            )}

            {/* 요청 JSON */}
            {detail.request != null && (
              <JsonSection title={t('integrationHub.history.detail.request')} data={detail.request} />
            )}

            {/* 응답 JSON */}
            {detail.response != null && (
              <JsonSection title={t('integrationHub.history.detail.response')} data={detail.response} />
            )}
          </>
        )}

        {/* 닫기 버튼 */}
        <div style={{ marginTop: 'auto', paddingTop: 'var(--spacing-4)' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('integrationHub.history.detail.close')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
        {label}
      </p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
        {value}
      </div>
    </div>
  );
}

function JsonSection({ title, data }: { title: string; data: Record<string, unknown> }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)', fontWeight: 600 }}>
        {title}
      </p>
      <pre
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-3)',
          fontSize: 'var(--font-size-xs)',
          fontFamily: 'var(--font-family-mono)',
          overflowX: 'auto',
          color: 'var(--color-text-primary)',
          maxHeight: 200,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          margin: 0,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
